import { z } from 'zod'

const color = z.string().regex(/^#[\da-f]{6}$/i, 'Ange en färg som #123456')
const number = (min: number, max: number, fallback: number): z.ZodDefault<z.ZodNumber> => z.number().finite().min(min).max(max).default(fallback)
const integer = (min: number, max: number, fallback: number): z.ZodDefault<z.ZodNumber> => z.number().int().min(min).max(max).default(fallback)
export const screenSchema = z.object({
  layout: z.enum(['compact', 'normal', 'large']).default('normal'),
  colorScheme: z.enum(['dark', 'light', 'high-contrast']).default('dark'),
  columns: z.number().int().min(1).max(8).nullable().default(null),
  fontSizeMultiplier: number(50, 250, 100),
  titleOverride: z.string().nullable().default(null),
  fontFamily: z.enum(['system', 'inter', 'roboto', 'helvetica', 'arial', 'georgia', 'times', 'courier']).default('system'),
  bold: z.boolean().default(false),
  italic: z.boolean().default(false),
  displayMode: z.enum(['auto', 'primary', 'secondary', 'both']).default('auto'),
  showSlipCompleteButton: z.enum(['default', 'on', 'off']).default('default'),
  showItemCounter: z.boolean().default(false),
  articleCounterOnly: z.boolean().default(false),
  itemCounterWidth: integer(180, 800, 320),
  itemCounterRowSize: number(50, 200, 100),
  itemCounterIncludeAlternatives: z.boolean().default(true),
  secondaryOpacity: number(10, 100, 60),
  pollIntervalSeconds: integer(2, 120, 5),
  hideBumpedAfterSeconds: integer(0, 86400, 0),
  preorderThresholdMinutes: integer(0, 10080, 30),
  autoMarkPickedUpAfterSec: integer(0, 86400, 0),
  keepPaidSlips: z.boolean().default(false),
  keepPaidSlipsAutoExpireMin: integer(1, 1440, 60),
  slipFullHeight: z.boolean().default(true),
  slipMaxHeight: integer(150, 2000, 600),
  treatAllAsPrimary: z.boolean().default(false),
  soundOnNewOrder: z.boolean().default(true),
  soundVolume: number(0, 100, 70),
  showOnlyChannels: z.array(z.string()).default([]),
  automaticallyPrintNewSlips: z.boolean().default(false),
  showHistory: z.boolean().default(true),
  showTicketTime: z.boolean().default(true),
})
const mapping = z.object({ productName: z.string().min(1), ratio: z.number().finite().positive() })
export const counterSchema = z.object({
  enabled: z.boolean().default(false),
  printWorkflowEnabled: z.boolean().default(false),
  excludedProductNames: z.array(z.string()).default([]),
  optionMappings: z.record(z.string(), mapping).default({}),
  productMappings: z.record(z.string(), mapping).default({}),
  bundleMappings: z.record(z.string(), z.array(mapping)).default({}),
  comboMappings: z.array(z.object({ productName: z.string(), optionName: z.string(), countAs: z.string(), ratio: z.number().finite().positive() })).default([]),
})
export const labelSchema = z.object({
  enabled: z.boolean().default(false),
  printerQueueId: z.string().nullable().default(null),
  widthMm: number(20, 120, 58),
  heightMm: number(20, 150, 40),
  channels: z.array(z.string()).default(['TakeAway', 'Delivery', 'Wolt', 'UberEats', 'DeliveryHero']),
  productIds: z.array(z.string()).default([]),
  alwaysPrintProductIds: z.array(z.string()).default([]),
})
export type PackingLabels = z.infer<typeof labelSchema>
export function packingLabels(defaults: unknown, overrides: unknown = null): PackingLabels {
  const base = document(defaults)
  const own = document(overrides)
  return labelSchema.parse({ ...base, ...own })
}
export const preparationSchema = z.object({ autoCompleteProductNames: z.array(z.string()).default([]) })
export const tillColorsSchema = z.record(z.string(), z.object({ color, label: z.string().nullable().default(null) }))
export type ScreenSettings = z.infer<typeof screenSchema>
export type ScreenOverrides = Partial<ScreenSettings>
export interface RestaurantAttributes {
  screenDefaults?: unknown
  counter?: unknown
  preparation?: unknown
  tillColors?: unknown
  labels?: unknown
}

function document(value: unknown): Record<string, unknown> {
  const row = z.record(z.string(), z.unknown()).parse(value ?? {})
  if (row.version != null && row.version !== 1) throw new Error('Skärmens inställningar har en version som inte stöds')
  return row
}
/** Keep only explicitly stored fields; schema defaults must never become overrides. */
export function readScreenOverrides(value: unknown): ScreenOverrides {
  const raw = document(value)
  const parsed = screenSchema.partial().parse(raw)
  const overrides: ScreenOverrides = {}
  for (const [key, next] of Object.entries(parsed)) if (Object.getOwnPropertyDescriptor(raw, key) != null) Object.assign(overrides, { [key]: next })
  return overrides
}
export function readConfiguration(attributes: RestaurantAttributes = {}): Configuration {
  const preparation = preparationSchema.parse(document(attributes.preparation))
  return {
    screenDefaults: screenSchema.parse(document(attributes.screenDefaults)),
    counter: { ...counterSchema.parse(document(attributes.counter)), autoCompleteProductNames: preparation.autoCompleteProductNames },
    preparation,
    labels: packingLabels(attributes.labels),
    tillColors: Object.entries(tillColorsSchema.parse(attributes.tillColors ?? {})).map(([tillId, value]) => ({ tillId, ...value })),
  }
}
export interface Configuration {
  screenDefaults: ScreenSettings
  counter: z.infer<typeof counterSchema> & z.infer<typeof preparationSchema>
  preparation: z.infer<typeof preparationSchema>
  labels: PackingLabels
  tillColors: { tillId: string; color: string; label: string | null }[]
}
export function screenSettings(config: Configuration, overrides?: unknown): ScreenSettings {
  return { ...config.screenDefaults, ...readScreenOverrides(overrides) }
}
/** Write only this attribute, retaining fields from newer clients. */
export function attributeDocument(previous: unknown, next: object): Record<string, unknown> {
  return { ...document(previous), ...next, version: 1 }
}
/** Store differences only, so unchanged settings keep inheriting future restaurant defaults. */
export function screenDifferences(defaults: ScreenSettings, next: ScreenSettings): ScreenOverrides {
  const differences: ScreenOverrides = {}
  for (const [key, value] of Object.entries(next)) {
    if (JSON.stringify(value) !== JSON.stringify(Object.getOwnPropertyDescriptor(defaults, key)?.value)) Object.assign(differences, { [key]: value })
  }
  return differences
}

export function screenAttribute(previous: unknown, next: ScreenOverrides): Record<string, unknown> {
  const preserved = Object.fromEntries(Object.entries(document(previous)).filter(([key]) => !(key in screenSchema.shape)))
  return { ...preserved, ...next, version: 1 }
}
export function labelAttribute(previous: unknown, defaults: PackingLabels, next: PackingLabels): Record<string, unknown> {
  const preserved = Object.fromEntries(Object.entries(document(previous)).filter(([key]) => !(key in labelSchema.shape)))
  for (const [key, value] of Object.entries(next)) {
    if (JSON.stringify(value) !== JSON.stringify(Object.getOwnPropertyDescriptor(defaults, key)?.value)) preserved[key] = value
  }
  return { ...preserved, version: 1 }
}
